import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  gigId: string;
  subjectId: string;
  direction: "employer_to_worker" | "worker_to_employer";
  onSubmitted?: () => void;
}

const StarRow = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-0.5"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star
            className={`h-5 w-5 ${
              n <= value ? "fill-accent text-accent" : "text-muted-foreground"
            }`}
          />
        </button>
      ))}
    </div>
  </div>
);

export const ReviewForm = ({ gigId, subjectId, direction, onSubmitted }: Props) => {
  const [stars, setStars] = useState(5);
  const [prof, setProf] = useState(5);
  const [comm, setComm] = useState(5);
  const [rel, setRel] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const { error } = await supabase.from("gig_reviews").insert({
      gig_id: gigId,
      subject_id: subjectId,
      reviewer_id: (await supabase.auth.getUser()).data.user?.id,
      direction,
      stars,
      professionalism: prof,
      communication: comm,
      reliability: rel,
      comment: comment.trim() || null,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Review submitted");
    onSubmitted?.();
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-4 space-y-3">
      <p className="text-sm font-semibold">
        Leave a review {direction === "employer_to_worker" ? "for the worker" : "for the employer"}
      </p>
      <StarRow label="Overall" value={stars} onChange={setStars} />
      <StarRow label="Professionalism" value={prof} onChange={setProf} />
      <StarRow label="Communication" value={comm} onChange={setComm} />
      <StarRow label="Reliability" value={rel} onChange={setRel} />
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Optional comment"
        rows={3}
        maxLength={500}
      />
      <Button onClick={submit} disabled={busy} className="w-full rounded-full">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit review
      </Button>
    </div>
  );
};
